import { useMemo } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import {
  ShieldAlert,
  Ghost,
  Radio,
  ExternalLink,
  ArchiveRestore,
  Undo2,
  CheckCircle2,
} from 'lucide-react';
import type { DataHealthAnomaly, DataHealthAnomalyKind, DataHealthResponse } from '@siliconx/shared';
import { api, throwIfNotOk } from '../lib/api';
import { formatUsd } from '../lib/format';
import { useAuth } from '../lib/authContext';
import { FinancialsReportScreen } from '../components/financials/FinancialsReportScreen';
import { CalculationBasisTrigger } from '../components/financials/CalculationBasisDrawer';
import { toast } from 'sonner';
import { qk } from '../lib/queryKeys';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-303 (W4, spec slice 2 "Data Health - /data-health"). Consumes
 * `GET /api/client/data-health` (SIL-294) — zombie/ghost/stale anomaly cards
 * + evidence-scoped ack + existing lifecycle mutations for "Mark retired" /
 * "Reinstate on books". No new write path here beyond the existing ack
 * endpoint — Mark retired / Reinstate reuse
 * `PATCH /api/client/assets/:assetId/lifecycle-state` (already merged).
 */
// SIL-1108: `orgFeature: 'fleetData'` (customerNav.ts) is business-only —
// gated here so a direct hit doesn't fire this page's mount-time fetch
// before the root `OrgFeatureGate`'s (__root.tsx) redirect lands.
export const Route = createFileRoute('/data-health')({
  beforeLoad: () => requireOrgFeature('fleetData'),
  component: DataHealthPage,
});

const KIND_CONFIG: Record<
  DataHealthAnomalyKind,
  { label: string; icon: typeof ShieldAlert; description: string; accent: string }
> = {
  zombie: {
    label: 'Zombie assets',
    icon: ShieldAlert,
    description: 'On the books, dark in monitoring — net book value still depreciating.',
    accent: 'text-red-600 bg-red-50',
  },
  ghost: {
    label: 'Ghost assets',
    icon: Ghost,
    description: 'Telemetry active, retired in ERP — FMV currently unbooked.',
    accent: 'text-violet-600 bg-violet-50',
  },
  stale: {
    label: 'Stale telemetry',
    icon: Radio,
    description: 'No telemetry data — FMV confidence drops one band.',
    accent: 'text-amber-600 bg-amber-50',
  },
};

function DataHealthPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();

  const dataHealthQ = useQuery<DataHealthResponse>({
    queryKey: qk.dataHealth(),
    queryFn: async () => {
      const res = await api.api.client['data-health'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as DataHealthResponse;
    },
  });

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: qk.dataHealth() });

  const lifecycleMutation = useMutation<
    { id: string; lifecycleStateId: string },
    Error & { status?: number },
    { assetId: string; lifecycleStateId: 'decommissioned' | 'active' }
  >({
    mutationFn: async ({ assetId, lifecycleStateId }) => {
      const res = await api.api.client.assets[':assetId']['lifecycle-state'].$patch({
        param: { assetId },
        json: { lifecycleStateId },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { id: string; lifecycleStateId: string };
    },
    onSuccess: (_, vars) => {
      toast.success(
        vars.lifecycleStateId === 'decommissioned' ? 'Asset marked retired.' : 'Asset reinstated on books.',
      );
      invalidate();
    },
    onError: (err) => toast.error(err.message ?? 'Lifecycle update failed.'),
  });

  const ackMutation = useMutation<
    { ackedCount: number },
    Error & { status?: number },
    { assetId: string; anomalyKind: DataHealthAnomalyKind }
  >({
    mutationFn: async ({ assetId, anomalyKind }) => {
      const res = await api.api.client['data-health'].ack.$post({
        json: { assetIds: [assetId], anomalyKind },
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as { ackedCount: number };
    },
    onSuccess: () => {
      toast.success('Acknowledged — this anomaly re-flags automatically if the evidence changes.');
      invalidate();
    },
    onError: (err) => toast.error(err.message ?? 'Acknowledge failed.'),
  });

  const anomaliesByKind = useMemo(() => {
    const grouped: Record<DataHealthAnomalyKind, DataHealthAnomaly[]> = {
      zombie: [],
      ghost: [],
      stale: [],
    };
    for (const a of dataHealthQ.data?.anomalies ?? []) {
      grouped[a.anomalyKind].push(a);
    }
    return grouped;
  }, [dataHealthQ.data]);

  const flaggedAssetCount = useMemo(() => {
    const ids = new Set((dataHealthQ.data?.anomalies ?? []).map((a) => a.assetId));
    return ids.size;
  }, [dataHealthQ.data]);

  return (
    <FinancialsReportScreen
      title="Data Health"
      pinTo="/data-health"
      subtitle="Assets where telemetry, ERP status, and lifecycle state disagree."
      query={dataHealthQ}
      errorTitle="Couldn't load data health report."
    >
      {(data) => (
        <>
          <SummaryPanel
            flaggedAssetCount={flaggedAssetCount}
            counts={data.counts}
            basis={data.basis}
          />

          <div className="space-y-5">
            {(Object.keys(KIND_CONFIG) as DataHealthAnomalyKind[]).map((kind) => (
              <AnomalyCard
                key={kind}
                kind={kind}
                anomalies={anomaliesByKind[kind]}
                isAdmin={isAdmin}
                onMarkRetired={(assetId) =>
                  lifecycleMutation.mutate({ assetId, lifecycleStateId: 'decommissioned' })
                }
                onReinstate={(assetId) =>
                  lifecycleMutation.mutate({ assetId, lifecycleStateId: 'active' })
                }
                onAck={(assetId) => ackMutation.mutate({ assetId, anomalyKind: kind })}
                mutating={lifecycleMutation.isPending || ackMutation.isPending}
              />
            ))}
          </div>
        </>
      )}
    </FinancialsReportScreen>
  );
}

// ---------------------------------------------------------------------------
// Summary panel — "N ASSETS - M ANOMALIES" header + basis affordance.
// ---------------------------------------------------------------------------

function SummaryPanel({
  flaggedAssetCount,
  counts,
  basis,
}: {
  flaggedAssetCount: number;
  counts: DataHealthResponse['counts'];
  basis: DataHealthResponse['basis'];
}) {
  const clean = counts.total === 0;

  return (
    <Card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-1.5">
            <SectionLabel>
              Fleet audit
            </SectionLabel>
            <CalculationBasisTrigger title="Data Health thresholds" basis={basis} align="right" />
          </div>
          <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">
            {flaggedAssetCount.toLocaleString()} asset{flaggedAssetCount === 1 ? '' : 's'} —{' '}
            {counts.total.toLocaleString()} anomal{counts.total === 1 ? 'y' : 'ies'}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {clean
              ? 'No anomalies detected — telemetry, ERP status, and lifecycle state agree.'
              : 'Acknowledged anomalies are excluded; a resurfaced anomaly re-flags on its own evidence.'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(KIND_CONFIG) as DataHealthAnomalyKind[]).map((kind) => {
            const cfg = KIND_CONFIG[kind];
            return (
              <span
                key={kind}
                className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${cfg.accent}`}
              >
                <cfg.icon className="size-3.5" />
                {counts[kind]} {cfg.label}
              </span>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Anomaly card — one per kind, list of asset rows + actions.
// ---------------------------------------------------------------------------

function AnomalyCard({
  kind,
  anomalies,
  isAdmin,
  onMarkRetired,
  onReinstate,
  onAck,
  mutating,
}: {
  kind: DataHealthAnomalyKind;
  anomalies: DataHealthAnomaly[];
  isAdmin: boolean;
  onMarkRetired: (assetId: string) => void;
  onReinstate: (assetId: string) => void;
  onAck: (assetId: string) => void;
  mutating: boolean;
}) {
  const cfg = KIND_CONFIG[kind];

  return (
    <Card>
      <div className="flex items-center gap-2">
        <span className={`flex size-8 shrink-0 items-center justify-center rounded-full ${cfg.accent}`}>
          <cfg.icon className="size-4" />
        </span>
        <div>
          <h2 className="text-[15px] font-semibold text-foreground">
            {cfg.label} <span className="text-muted-foreground">({anomalies.length})</span>
          </h2>
          <p className="text-xs text-muted-foreground">{cfg.description}</p>
        </div>
      </div>

      {anomalies.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">Nothing flagged.</p>
      ) : (
        <div className="mt-4 divide-y divide-border">
          {anomalies.map((a) => (
            <AnomalyRow
              key={a.assetId}
              kind={kind}
              anomaly={a}
              isAdmin={isAdmin}
              onMarkRetired={onMarkRetired}
              onReinstate={onReinstate}
              onAck={onAck}
              mutating={mutating}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function assetIdentity(a: DataHealthAnomaly): string {
  return a.serialNumber ?? a.assetTag ?? a.assetId.slice(0, 8);
}

function AnomalyRow({
  kind,
  anomaly,
  isAdmin,
  onMarkRetired,
  onReinstate,
  onAck,
  mutating,
}: {
  kind: DataHealthAnomalyKind;
  anomaly: DataHealthAnomaly;
  isAdmin: boolean;
  onMarkRetired: (assetId: string) => void;
  onReinstate: (assetId: string) => void;
  onAck: (assetId: string) => void;
  mutating: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{assetIdentity(anomaly)}</span>
          {anomaly.assetTag && anomaly.serialNumber && (
            <span className="truncate text-xs text-muted-foreground">{anomaly.assetTag}</span>
          )}
        </div>
        <p className="mt-0.5 text-xs text-muted-foreground">{evidenceLine(kind, anomaly)}</p>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <Link
          to="/assets/$assetId"
          params={{ assetId: anomaly.assetId }}
          className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
        >
          Investigate
          <ExternalLink className="size-3" />
        </Link>

        {isAdmin && kind === 'zombie' && (
          <button
            type="button"
            disabled={mutating}
            onClick={() => onMarkRetired(anomaly.assetId)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            <ArchiveRestore className="size-3.5" />
            Mark retired
          </button>
        )}

        {isAdmin && kind === 'ghost' && (
          <button
            type="button"
            disabled={mutating}
            onClick={() => onReinstate(anomaly.assetId)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            <Undo2 className="size-3.5" />
            Reinstate on books
          </button>
        )}

        {isAdmin && (
          <button
            type="button"
            disabled={mutating}
            onClick={() => onAck(anomaly.assetId)}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            <CheckCircle2 className="size-3.5" />
            {kind === 'ghost' ? 'Confirm retirement' : 'Acknowledge'}
          </button>
        )}
      </div>
    </div>
  );
}

function evidenceLine(kind: DataHealthAnomalyKind, a: DataHealthAnomaly): string {
  const nbv = a.netBookValueUsd !== null ? formatUsd(a.netBookValueUsd) : 'unknown';
  const days = a.daysDark;

  switch (kind) {
    case 'zombie':
      return `On the books, dark in monitoring ${days === null ? '' : `for ${days} day${days === 1 ? '' : 's'} `}— net book value ${nbv}, still depreciating.`;
    case 'ghost':
      return `Telemetry active${days !== null ? ` (last seen ${days} day${days === 1 ? '' : 's'} ago)` : ''}, retired in ERP — FMV currently unbooked.`;
    case 'stale':
      return `No telemetry data${days === null ? '' : ` for ${days} day${days === 1 ? '' : 's'}`} — FMV confidence drops one band.`;
    default:
      return '';
  }
}
